/**
 * BuildTrackPhase - 線路建設フェーズUIコンポーネント
 *
 * FRONTEND.md Step.3 準拠:
 * - Lovable スタイル適用済み
 *
 * VISUAL_BLUEPRINT 3.4 要件:
 * - 15種のタイルが横3×縦5表示
 * - 各タイルの右下に残り枚数表示
 * - グレーアウト: 残り0枚 / 資金不足 / 新都市タイルでUrbanization未選択
 * - 縦スクロール対応（高さ制限時）
 *
 * @see FRONTEND.md
 * @see src/types/ui-interfaces.ts
 */

import type { BuildTrackPhaseProps } from '../../types/ui-interfaces';
import { useTranslations } from '../../i18n';

/**
 * ヘクスの頂点座標を計算（Pointy-top orientation）
 *
 * Pointy-top hex は上部が角（頂点）:
 *        5  0
 *         \/
 *      4-[HEX]-1
 *         /\
 *        3  2
 *
 * 頂点はコンパス方位角0°（北=上）から始まり、時計回りに60°ずつ配置
 * コンパス角θ → 画面座標: x = sin(θ), y = -cos(θ)
 */
function getHexPoints(cx: number, cy: number, size: number): string {
  const points: string[] = [];
  for (let i = 0; i < 6; i++) {
    // 頂点のコンパス方位角: 0°, 60°, 120°, 180°, 240°, 300°
    const compassAngle = (i * 60) * Math.PI / 180;
    const x = cx + size * Math.sin(compassAngle);
    const y = cy - size * Math.cos(compassAngle);
    points.push(`${x},${y}`);
  }
  return points.join(' ');
}

/**
 * 辺の中点座標を計算（Pointy-top orientation）
 *
 * 辺番号と角度（コンパス方位角: 北=0°、時計回りが正）:
 * - 0 = NE（北東） = 30°
 * - 1 = E（東）   = 90°
 * - 2 = SE（南東） = 150°
 * - 3 = SW（南西） = 210°
 * - 4 = W（西）   = 270°
 * - 5 = NW（北西） = 330°
 *
 * コンパス角θ → 画面座標: x = sin(θ), y = -cos(θ)
 */
function getEdgeMidpoint(cx: number, cy: number, size: number, edge: number): { x: number; y: number } {
  const compassAngles = [30, 90, 150, 210, 270, 330];
  const θ = compassAngles[edge] * Math.PI / 180;
  // 中心から辺の中点までの距離 = size * cos(30°)
  const distance = size * Math.cos(Math.PI / 6);
  return {
    x: cx + distance * Math.sin(θ),
    y: cy - distance * Math.cos(θ),
  };
}

/**
 * タイルの線路パターンをヘクス形状で表示するSVGアイコン
 * docs/tiles.ts の接続情報を使用
 */
function HexTileIcon({ tileType, size = 48 }: { tileType: string; size?: number }) {
  const strokeColor = 'hsl(var(--foreground))';
  const trackColor = 'hsl(45, 60%, 35%)'; // 茶色の線路
  const strokeWidth = 2;
  const trackWidth = 4;
  const cx = size / 2;
  const cy = size / 2;
  const hexSize = size * 0.42; // ヘクスのサイズ

  // タイルタイプに応じた接続情報
  const getConnections = (): number[][] => {
    const connectionMap: Record<string, number[][]> = {
      // Simple Tiles
      'STRAIGHT': [[0, 3]],
      'ACUTE_CURVE': [[0, 1]],
      'GENTLE_CURVE': [[0, 2]],
      // Crossing Tiles
      'GENTLE_STRAIGHT_CROSS': [[0, 3], [1, 5]],
      'TWO_GENTLE_CROSS': [[0, 2], [1, 3]],
      'TWO_STRAIGHT_CROSS': [[0, 3], [1, 4]],
      // Coexist Tiles
      'TWO_GENTLE_COEXIST': [[0, 2], [3, 5]],
      'LEFT_GENTLE_ACUTE': [[0, 2], [3, 4]],
      'RIGHT_GENTLE_ACUTE': [[0, 1], [2, 4]],
      'STRAIGHT_ACUTE': [[0, 3], [4, 5]],
      // Town Tiles
      'TOWN_1': [[0]],
      'TOWN_3_LEFT': [[0], [3], [4]],
      'TOWN_3_RIGHT': [[0], [2], [3]],
      'TOWN_3_HALF': [[0], [1], [2]],
      'TOWN_3_STAR': [[0], [2], [4]],
    };
    return connectionMap[tileType] || [];
  };

  const connections = getConnections();
  const isTownTile = tileType.startsWith('TOWN_');
  const isNewCity = tileType.startsWith('NEW_CITY_');

  // 新都市タイルの色
  const getNewCityColor = (): string => {
    const colorMap: Record<string, string> = {
      'NEW_CITY_A': 'hsl(0, 70%, 50%)',   // red
      'NEW_CITY_B': 'hsl(210, 70%, 50%)', // blue
      'NEW_CITY_C': 'hsl(0, 0%, 25%)',    // black
      'NEW_CITY_D': 'hsl(0, 0%, 25%)',    // black
      'NEW_CITY_E': 'hsl(45, 80%, 55%)',  // yellow
      'NEW_CITY_F': 'hsl(270, 50%, 55%)', // purple
      'NEW_CITY_G': 'hsl(0, 0%, 25%)',    // black
      'NEW_CITY_H': 'hsl(0, 0%, 25%)',    // black
    };
    return colorMap[tileType] || 'hsl(0, 0%, 50%)';
  };

  // 線路パスを生成
  const renderTracks = () => {
    return connections.map((conn, idx) => {
      if (conn.length === 1) {
        // 町タイル: 辺から中心への線
        const edge = getEdgeMidpoint(cx, cy, hexSize, conn[0]);
        return (
          <line
            key={idx}
            x1={edge.x}
            y1={edge.y}
            x2={cx}
            y2={cy}
            stroke={trackColor}
            strokeWidth={trackWidth}
            strokeLinecap="round"
          />
        );
      } else if (conn.length === 2) {
        // 通常タイル: 辺から辺への線
        const edge1 = getEdgeMidpoint(cx, cy, hexSize, conn[0]);
        const edge2 = getEdgeMidpoint(cx, cy, hexSize, conn[1]);

        // 直線か曲線かを判定（対角なら直線、それ以外は曲線）
        const diff = Math.abs(conn[0] - conn[1]);
        const isStraight = diff === 3;

        if (isStraight) {
          return (
            <line
              key={idx}
              x1={edge1.x}
              y1={edge1.y}
              x2={edge2.x}
              y2={edge2.y}
              stroke={trackColor}
              strokeWidth={trackWidth}
              strokeLinecap="round"
            />
          );
        } else {
          // 曲線: ベジェ曲線で描画
          const controlX = cx;
          const controlY = cy;
          return (
            <path
              key={idx}
              d={`M ${edge1.x} ${edge1.y} Q ${controlX} ${controlY} ${edge2.x} ${edge2.y}`}
              fill="none"
              stroke={trackColor}
              strokeWidth={trackWidth}
              strokeLinecap="round"
            />
          );
        }
      }
      return null;
    });
  };

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="mx-auto">
      {/* ヘクス背景 */}
      <polygon
        points={getHexPoints(cx, cy, hexSize)}
        fill={isNewCity ? getNewCityColor() : 'hsl(var(--card))'}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
      />

      {/* 線路パターン */}
      {!isNewCity && renderTracks()}

      {/* 町タイルの中心円 */}
      {isTownTile && (
        <circle
          cx={cx}
          cy={cy}
          r={hexSize * 0.35}
          fill="white"
          stroke={strokeColor}
          strokeWidth={1.5}
        />
      )}

      {/* 新都市タイルのラベル */}
      {isNewCity && (
        <>
          <circle
            cx={cx}
            cy={cy}
            r={hexSize * 0.5}
            fill="white"
            stroke={strokeColor}
            strokeWidth={1.5}
          />
          <text
            x={cx}
            y={cy + hexSize * 0.15}
            textAnchor="middle"
            fontSize={hexSize * 0.6}
            fontWeight="bold"
            fill="hsl(var(--foreground))"
          >
            {tileType.replace('NEW_CITY_', '')}
          </text>
        </>
      )}
    </svg>
  );
}

/**
 * Phase 4: 線路建設フェーズUI（共通）
 *
 * 機能:
 * - タイル選択（18種類）
 * - 仮配置とリアルタイムバリデーション（マップクリックで回転）
 * - 配置確定と手番終了
 * - Cancelでこのフェーズ中の全配置を撤去
 */
export function BuildTrackPhase({
  player,
  maxTilesThisTurn,
  availableTiles,
  isMyTurn,
  temporaryPlacement,
  temporaryLinkPlacement,
  selectedTileType,
  townDisksRemaining,
  buildCostInfo,
  onSelectTile,
  onSetTemporaryPlacement,
  onConfirmPlacement,
  onFinishBuild,
  onCancelAllPlacements,
}: BuildTrackPhaseProps & { onCancelAllPlacements?: () => void }) {
  const t = useTranslations();

  // 配置確定可能かどうか（タイルまたは特殊リンク）
  const canConfirmTile = temporaryPlacement && !temporaryPlacement.validationError;
  const canConfirmLink = temporaryLinkPlacement && !temporaryLinkPlacement.validationError;
  const canConfirm = canConfirmTile || canConfirmLink;

  // 所持金オーバーかどうか（一括払い方式）
  const isOverBudget = buildCostInfo?.isOverBudget ?? false;

  return (
    <div className="flex flex-col h-full">
      {/* コンテンツエリア */}
      <div className="flex-1 min-h-0 flex flex-col pb-2">
        {!isMyTurn ? (
          <p className="text-muted-foreground text-sm">{t.common.waiting}</p>
        ) : (
          <>
            {/* 操作説明（折りたたみ） */}
            <details className="mb-1 text-xs text-muted-foreground">
              <summary className="cursor-pointer select-none hover:text-foreground transition-colors">
                {t.buildTrack.help.title}
              </summary>
              <ul className="mt-1 ml-4 space-y-0.5 list-disc">
                <li>{t.buildTrack.help.placeTile}</li>
                <li>{t.buildTrack.help.cancelTile}</li>
                <li>{t.buildTrack.help.finishBuild}</li>
                <li>{t.buildTrack.help.specialLink}</li>
              </ul>
            </details>

            {/* 支払いコスト表示 */}
            {buildCostInfo && buildCostInfo.totalCost > 0 && (
              <div className={`text-sm mb-1 text-center font-medium ${isOverBudget ? 'text-destructive' : ''}`}>
                {t.buildTrack.payment}: ${buildCostInfo.finalCost}
                {buildCostInfo.discount > 0 && (
                  <span className="text-muted-foreground ml-1">
                    (${buildCostInfo.totalCost} - ${buildCostInfo.discount})
                  </span>
                )}
                {isOverBudget && (
                  <span className="ml-2 text-destructive">{t.buildTrack.overBudget}</span>
                )}
              </div>
            )}

            {/* 残り敷設可能枚数 & Town Disk 残数 */}
            <div className="text-xs text-muted-foreground mb-1 text-center flex justify-center gap-3">
              <span>
                {t.buildTrack.trackTiles}{' '}
                <span className="text-lg font-semibold">
                  {player.tilesPlacedThisTurn}/{maxTilesThisTurn}
                </span>
              </span>
              {player.selectedAction === 'URBANIZATION' && (
                <span>
                  {t.buildTrack.cityTiles}{' '}
                  <span className="text-lg font-semibold">
                    {player.newCityTilesPlacedThisTurn}/1
                  </span>
                </span>
              )}
              <span className={townDisksRemaining <= 2 ? 'text-warning' : ''}>
                {t.buildTrack.townDisk}{' '}
                <span className="text-lg font-semibold">
                  {townDisksRemaining}/8
                </span>
              </span>
            </div>
            {/* タイル選択グリッド: 横4列、残りスペースを使用 */}
            <div className="flex-1 min-h-0 overflow-y-auto pr-1">
              <div className="grid grid-cols-4 gap-1.5">
                {availableTiles.map((tile) => {
                  const isDisabled = tile.remaining === 0 || (tile.isNewCity && player.selectedAction !== 'URBANIZATION');
                  const isSelected = selectedTileType === tile.type;

                  return (
                    <button
                      key={tile.type}
                      title={tile.name}
                      className={`
                        relative p-1.5 rounded-md border-2 transition-all aspect-square
                        ${isSelected
                          ? 'border-primary bg-primary/10 ring-2 ring-primary/30'
                          : 'border-border bg-card hover:border-primary/50 hover:bg-muted/50'
                        }
                        ${isDisabled
                          ? 'opacity-40 cursor-not-allowed grayscale'
                          : 'cursor-pointer'
                        }
                      `}
                      onClick={() => !isDisabled && onSelectTile(tile.type)}
                      disabled={isDisabled}
                      data-selected={isSelected}
                      data-disabled={isDisabled}
                    >
                      {/* ヘクス形状タイルアイコン（マップと同サイズ） */}
                      <HexTileIcon tileType={tile.type} size={52} />

                      {/* 残り枚数（右下に大きく表示） */}
                      <div className={`
                        absolute bottom-0.5 right-0.5 text-xs font-bold font-mono min-w-[18px] h-[18px]
                        flex items-center justify-center rounded
                        ${tile.remaining === 0
                          ? 'bg-destructive text-destructive-foreground'
                          : isSelected
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted-foreground/80 text-card'
                        }
                      `}>
                        {isDisabled && tile.isNewCity ? '−' : tile.remaining}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>

      {/* 固定アクションボタン（スクロール外） */}
      {isMyTurn && (
        <div className="flex-shrink-0 grid grid-cols-3 gap-2 pt-2 border-t border-border bg-card">
          <button
            className="btn-ghost text-xs py-2"
            onClick={() => {
              onSetTemporaryPlacement(null);
              // 通常タイルまたは新都市タイルを配置済みの場合、全てキャンセル
              const hasPlacedAnyTile = player.tilesPlacedThisTurn > 0 || player.newCityTilesPlacedThisTurn > 0;
              if (hasPlacedAnyTile && onCancelAllPlacements) {
                onCancelAllPlacements();
              }
            }}
            disabled={!temporaryPlacement && player.tilesPlacedThisTurn === 0 && player.newCityTilesPlacedThisTurn === 0}
          >
            {t.common.cancel}
          </button>
          <button
            className="btn-primary text-xs py-2"
            onClick={onConfirmPlacement}
            disabled={!canConfirm}
          >
            {t.common.confirm}
          </button>
          <button
            className="btn-secondary text-xs py-2"
            onClick={onFinishBuild}
            disabled={isOverBudget}
          >
            {player.tilesPlacedThisTurn === 0 ? t.common.pass : t.common.finish}
          </button>
        </div>
      )}
    </div>
  );
}

export default BuildTrackPhase;
